import { forwardRef, useCallback } from 'react';

import {
  Alert,
  AlertColor,
  ClickAwayListener,
  IconButton,
  Typography,
} from '@mui/material';

import { CustomContentProps, SnackbarContent, useSnackbar } from 'notistack';

import {
  CheckCircle,
  CloseRounded,
  Error,
  Info,
  Warning,
} from '@mui/icons-material';

export const SnackbarAlert = forwardRef<HTMLDivElement, CustomContentProps>(
  (props, ref) => {
    const { closeSnackbar } = useSnackbar();
    const { id, message, variant = 'success' } = props;
    const severity = variant === 'default' ? 'info' : (variant as AlertColor);

    const handleDismiss = useCallback(
      () => closeSnackbar(id),
      [id, closeSnackbar],
    );
    const handleClickAway = useCallback(
      () => closeSnackbar(id),
      [id, closeSnackbar],
    );

    return (
      <SnackbarContent
        ref={ref}
        role={severity}
        style={{ justifyContent: 'center', alignItems: 'center' }}
      >
        <ClickAwayListener onClickAway={handleClickAway}>
          <Alert
            id={`local-notification-${id}`}
            severity={severity}
            iconMapping={{
              success: <CheckCircle />,
              error: <Error />,
              warning: <Warning />,
              info: <Info />,
            }}
            action={
              <IconButton
                aria-label="close"
                color="inherit"
                size="small"
                style={{ display: 'flex', alignSelf: 'center' }}
                onClick={handleDismiss}
              >
                <CloseRounded />
              </IconButton>
            }
            sx={{
              mb: 2,
              backgroundColor: 'white',
              borderRadius: '12px',
              color: 'text.primary',
              borderLeft: '12px solid',
              boxShadow: '0px 4px 8px 0px rgba(0, 0, 0, 0.08)',
              display: 'flex',
              alignItems: 'center',
              '&.MuiAlert-standardSuccess': {
                borderLeftColor: 'success.main',
              },
              '&.MuiAlert-standardError': {
                borderLeftColor: 'error.main',
              },
              '&.MuiAlert-standardWarning': {
                borderLeftColor: 'warning.main',
              },
              '&.MuiAlert-standardInfo': {
                borderLeftColor: 'primary.main',
              },
            }}
          >
            <Typography variant="body1" fontWeight={500}>
              {message}
            </Typography>
          </Alert>
        </ClickAwayListener>
      </SnackbarContent>
    );
  },
);
